import React, { useCallback, useEffect, useState } from "react";
import { Tabs, Typography, Button, message } from "antd";
import { PlusOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  getPatientOwnDocuments,
  getPatientCareTeamDocuments,
  uploadCareTeamDocument,
  downloadBloodWork,
} from "../../../redux/doctorSlice";
import Header from "../Components/Header";
import DocumentTable from "../../Documents/DocumentTable";
import DocumentUploadForm from "../../Documents/DocumentUploadForm";

const { Title } = Typography;

const SHARING_OPTIONS = [
  { label: "Private to Care Team", value: false },
  { label: "Shared with Patient", value: true },
];

const mapDoc = (file) => ({
  fileRef: file.fileRef,
  title: file.fileTitle,
  filename: file.filename,
  dateAdded: file.createdOn,
  isSharedWithPatient: file.isSharedWithPatient,
  providerName: file.providerName,
  providerProfilePhoto: file.providerProfilePhoto,
  providerInitials: file.providerInitials,
});

const getFileMime = (filename) => {
  const extension = filename?.split(".").pop()?.toLowerCase();
  if (extension === "png") return "image/png";
  if (["jpg", "jpeg"].includes(extension)) return "image/jpeg";
  return "application/pdf";
};

const DoctorDocuments = () => {
  const dispatch = useDispatch();
  const patient = JSON.parse(localStorage.getItem("patient") || "{}");
  const patientRef = patient?.userRef;

  const [activeTab, setActiveTab] = useState("careTeam");
  const [view, setView] = useState("list");
  const [careTeamDocs, setCareTeamDocs] = useState([]);
  const [patientDocs, setPatientDocs] = useState([]);
  const [loadingCareTeam, setLoadingCareTeam] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const fetchCareTeamDocuments = useCallback(async () => {
    if (!patientRef) return;
    setLoadingCareTeam(true);
    const result = await dispatch(getPatientCareTeamDocuments(patientRef));
    if (getPatientCareTeamDocuments.fulfilled.match(result)) {
      setCareTeamDocs((result.payload || []).map(mapDoc));
    } else {
      // Endpoint not built yet on the backend — show an honest empty state.
      setCareTeamDocs([]);
    }
    setLoadingCareTeam(false);
  }, [dispatch, patientRef]);

  const fetchPatientDocuments = useCallback(async () => {
    if (!patientRef) return;
    setLoadingPatient(true);
    const result = await dispatch(getPatientOwnDocuments(patientRef));
    if (getPatientOwnDocuments.fulfilled.match(result)) {
      setPatientDocs((result.payload || []).map(mapDoc));
    } else {
      message.error("Failed to load this patient's documents.");
    }
    setLoadingPatient(false);
  }, [dispatch, patientRef]);

  useEffect(() => {
    fetchCareTeamDocuments();
    fetchPatientDocuments();
  }, [fetchCareTeamDocuments, fetchPatientDocuments]);

  const handleDownload = async (fileRef, filename) => {
    const result = await dispatch(downloadBloodWork(fileRef));
    if (downloadBloodWork.fulfilled.match(result)) {
      const blob = new Blob([result.payload], { type: getFileMime(filename) });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || fileRef;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else {
      message.error("Failed to download document.");
    }
  };

  const handleUpload = async ({ title, base64, filename, isSharedWithPatient }) => {
    if (!patientRef) return;
    const result = await dispatch(
      uploadCareTeamDocument({ patientRef, base64, filename, fileTitle: title, isSharedWithPatient })
    );
    if (uploadCareTeamDocument.fulfilled.match(result)) {
      message.success("Document uploaded successfully.");
      setView("list");
      fetchCareTeamDocuments();
    } else {
      message.error("Failed to upload document.");
    }
  };

  const privateDocs = careTeamDocs.filter((d) => !d.isSharedWithPatient);
  const sharedDocs = careTeamDocs.filter((d) => d.isSharedWithPatient);

  return (
    <div style={{ padding: 24 }}>
      {patientRef ? (
        <Header />
      ) : (
        <div style={{ marginBottom: 20 }}>Select a patient to view their documents.</div>
      )}

      <Title level={4} style={{ color: "#335CAD", letterSpacing: 0.5 }}>DOCUMENTS</Title>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setView("list");
        }}
        items={[
          {
            key: "careTeam",
            label: "Care Team Documents",
            children:
              view === "upload" ? (
                <DocumentUploadForm
                  backLabel="Back to Care Team Documents"
                  onCancel={() => setView("list")}
                  onSubmit={handleUpload}
                  visibilityOptions={SHARING_OPTIONS}
                />
              ) : (
                <>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    style={{ paddingLeft: 0, marginBottom: 12 }}
                    disabled={!patientRef}
                    onClick={() => setView("upload")}
                  >
                    Upload Document
                  </Button>

                  <div style={{ marginBottom: 24 }}>
                    <Typography.Text strong>
                      <LockOutlined /> Private to Care Team ({privateDocs.length})
                    </Typography.Text>
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
                      Only visible to you and your team
                    </div>
                    <DocumentTable
                      documents={privateDocs}
                      loading={loadingCareTeam}
                      showProvider
                      onDownload={handleDownload}
                      emptyText="No private documents yet."
                    />
                  </div>

                  <div>
                    <Typography.Text strong>
                      <UnlockOutlined /> Shared with Patient ({sharedDocs.length})
                    </Typography.Text>
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 8 }}>
                      Visible to both you and the patient
                    </div>
                    <DocumentTable
                      documents={sharedDocs}
                      loading={loadingCareTeam}
                      showProvider
                      onDownload={handleDownload}
                      emptyText="No documents shared with the patient yet."
                    />
                  </div>
                </>
              ),
          },
          {
            key: "patient",
            label: "Patient Documents",
            children: (
              <DocumentTable
                documents={patientDocs}
                loading={loadingPatient}
                onDownload={handleDownload}
                emptyText="This patient hasn't uploaded any documents yet."
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default DoctorDocuments;
